const API_BASE = process.env.REACT_APP_API_BASE;

/**
 * Call the Modal backend's POST /predict endpoint.
 *
 * `input` can be:
 *   - a string URL                          → JSON {url}
 *   - a File / Blob                         → multipart upload
 *   - { imageB64: "data:image/...;base64,..." } → JSON {image_b64}
 *
 * Returns:
 *   { probability, verdict, detail, imageUrl, overlayUrl }
 *
 * imageUrl is the original image (echoed back when the caller already has it).
 * overlayUrl is a data: URL containing the Grad-CAM overlay PNG, or null.
 */
export async function predictImage(input) {
    if (!API_BASE) {
        throw new Error("REACT_APP_API_BASE is not configured");
    }

    let originalImageUrl = null;
    let fetchOpts;

    if (typeof input === "string") {
        originalImageUrl = input;
        fetchOpts = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ url: input }),
        };
    } else if (input instanceof Blob) {
        originalImageUrl = URL.createObjectURL(input);
        const form = new FormData();
        form.append("file", input, input.name || "upload.bin");
        fetchOpts = { method: "POST", body: form };
    } else if (input && typeof input.imageB64 === "string") {
        originalImageUrl = input.imageB64;
        fetchOpts = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_b64: input.imageB64 }),
        };
    } else {
        throw new Error("predictImage: unsupported input type");
    }

    const res = await fetch(`${API_BASE}/predict`, fetchOpts);

    let payload;
    try {
        payload = await res.json();
    } catch {
        throw new Error(`Prediction failed: HTTP ${res.status}`);
    }

    if (!res.ok) {
        const message = payload && payload.error
            ? payload.error
            : `HTTP ${res.status}`;
        throw new Error(message);
    }

    return {
        probability: payload.probability,
        verdict: payload.verdict,
        detail: payload.detail,
        imageUrl: originalImageUrl,
        overlayUrl: payload.overlay_b64 || null,
        modelVersion: payload.model_version,
        elapsedMs: payload.elapsed_ms,
    };
}
